import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TreeNode } from 'primereact/treenode';
import { TreeSelectProps } from 'primereact/treeselect';
import * as yup from 'yup';

import ButtonCustom from '@/components/common/ui/button';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { TreeSelectCustomForm } from '@/components/common/ui/treeSelect';
import { useTranslation } from '@/config/i18n';
import useSearch from '@/hooks/useSearch';
import { BaseYup, MAX_INPUT } from '@/config/baseYup';
import { parseQueryValue } from '@/lib/utils';

const mentorLessonSearchSchema = BaseYup.object({
  name: BaseYup.string().max(MAX_INPUT),
  status: BaseYup.string().max(MAX_INPUT).nullable(),
});

type MentorLessonSearchType = yup.InferType<typeof mentorLessonSearchSchema>;

type Props = {
  onSearch: () => void;
};

const MentorLessonSearch: React.FC<Props> = ({ onSearch }) => {
  const [t] = useTranslation('');
  const { handleSearch, queryRaw } = useSearch();

  const formMethods = useForm<MentorLessonSearchType>({
    mode: 'onChange',
    resolver: yupResolver(mentorLessonSearchSchema),
    shouldFocusError: false,
    defaultValues: {
      name: '',
      status: 'all',
    },
  });
  const { handleSubmit, getValues, setValue, reset } = formMethods;

  const statusTemplate = (
    selectedNodes: TreeNode | TreeNode[],
    props: TreeSelectProps,
  ) => {
    if (Array.isArray(selectedNodes) && selectedNodes.length > 0) {
      return (
        <>
          {t('user_list.status_label')}
          {selectedNodes[0].label}
        </>
      );
    }
    const defaultOption = props.options?.find(
      (option) => option.key === props.value,
    );
    return (
      <>
        {t('user_list.status_label')}
        {defaultOption?.label}
      </>
    );
  };

  // Restore the form from the URL so filters survive a reload.
  useEffect(() => {
    Object.keys(getValues()).forEach((field: any) => {
      const val = queryRaw[field];
      if (val) setValue(field, parseQueryValue(field, val));
    });
  }, []);

  function onSubmit() {
    const { name, status } = getValues();
    handleSearch({
      name,
      status: status === 'all' ? '' : status,
    });
    onSearch();
  }

  function handleReset() {
    reset();
    handleSearch({ reset: true });
    onSearch();
  }

  return (
    <FormProvider {...formMethods}>
      <form className="mt-[16px] flex" onSubmit={handleSubmit(onSubmit)}>
        <div className="search-input w-full relative">
          <span className="pi pi-search absolute top-[14px] left-[14px] text-gray-500 text-[15px] z-40" />
          <InputTextCustomForm
            name="name"
            className="!pl-[40px] h-[44px]"
            placeholder={t('lesson_list.name')}
          />
        </div>
        <TreeSelectCustomForm
          name="status"
          wrapClass="ml-4 w-[193px]"
          panelClassName="tree-hide-header tree-disabled-unselect"
          optionsStatic={[
            { key: 'all', label: t('common.status.all') },
            { key: 'active', label: t('common.status_other.active') },
            { key: 'inactive', label: t('common.status_other.inactive') },
          ]}
          valueTemplate={statusTemplate}
        />
        <div className="action flex ml-4">
          <ButtonCustom
            type="button"
            severity="secondary"
            onClick={handleReset}
            className="max-h-[44px]"
          >
            <span className="pi pi-refresh" />
          </ButtonCustom>
          <ButtonCustom type="submit" className="ml-2 max-h-[44px]">
            <span className="pi pi-search" />
          </ButtonCustom>
        </div>
      </form>
    </FormProvider>
  );
};

export default MentorLessonSearch;
